import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {Formik, Form} from 'formik'
import {useLocation} from 'react-router-dom'
import {PageTitle} from '../../../_metronic/layout/core'
import {
  ADDITIONAL_SERVICE_STATUS,
  IReservationModel,
  PRODUCT_OWNER,
  initialReservation,
  reservationSubTabOptions,
  validationReservation,
} from './ReservationModel'
import FormTabHeader, {TabOption} from '../../../shared/components/FormTabHeader'
import TextInput from '../../../shared/components/TextInput'
import SelectInput from '../../../shared/components/SelectInput'
import PhoneInput from '../../../shared/components/PhoneInput'
import TextAreaInput from '../../../shared/components/TextAreaInput'
import {showErrorToast, showSuccessToast} from '../../../shared/components/ToastUtils'
import {ToastContainer} from 'react-toastify' 
import {updateReservation, createReservation, getReservationById} from './Api'
import {
  branches,
  currencies,
  fetchAsyncCarData,
  fetchAsyncCustomerData,
  fetchAsyncReservationSourceData,
  fetchAsyncVehicleGroupData,
  personels,
  tariffs, 
} from '../../../shared/models/SharedApiLookups'
import AsyncSearchInput from '../../../shared/components/AsyncSearchInput'
import {
  AsyncCarModel,
  AsyncCustomerModel,
  AsyncReservationSourceModel,
  AsyncVehicleGroupModel,
} from '../../../shared/models/ApiModels'
import {
  CALCULATION_TYPES,
  CURRENCIES,
  DELIVERY_TYPES,
  FUEL_TYPES,
  OPTION_TYPES,
  PAYMENT_OWNER,
  PAYMENT_TYPES,
  RENTAL_TYPES,
  RESERVATION_STATUS,
  TRANSMISSION_TYPES,
} from '../../../shared/models/SharedConstantLookups'
import DateInput from '../../../shared/components/DateInput'
import CheckboxInput from '../../../shared/components/CheckboxInput'
import FormWatcher from './FormWatcher'
import AdditionalServicesCalculator from './AdditionalServicesCalculator'
import SubTabHeader from '../../../shared/components/SubTabHeader'
import {TabNavigationButtons} from '../../../shared/components/TabNavigationButtons'
import {CustomerCreateModal} from '../../../shared/modals/CustomerCreateModal'
import {useSelector} from 'react-redux'
import {RootState} from '../../../setup'
import {IPersonnelModel} from '../definition/personnel/PersonnelModel'
import {getPersonnelByUserId} from '../definition/personnel/Api'
import CalculateRentalPrice from './CalculateRentalPrice'

export const ReservationPage: FC = () => {
  const [tab, setTab] = useState<Tabs>('quick')
  const [tabSub, setTabSub] = useState<string | 'desc'>('desc')
  const [loading, setLoading] = useState(false)
  const location = useLocation()
  const reservationIdFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search)
    return q.get('reservationId')?.trim() || ''
  }, [location.search])
  const [selectedId, setSelectedId] = useState<string | null>(reservationIdFromUrl || null)
  const [initialFormValues, setInitialFormValues] =
    useState<IReservationModel>(initialReservation)
  const [branchOptions, setBranchOptions] = useState<{value: string; label: string}[]>([])
  const [tariffOptions, setTariffOptions] = useState<{value: string; label: string}[]>([])
  const [currencyOptions, setCurrencyOptions] = useState<{value: string; label: string}[]>([])
  const [personelOptions, setPersonelOptions] = useState<{value: string; label: string}[]>([])
  const [remainingAmount, setRemainingAmount] = useState<number | 0>(0)
  const [currency, setCurrency] = useState<string | 'TL'>('TL')
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [isSelectedResSource, setIsSelectedResSource] = useState(false)
  const [isManualPrice, setIsManualPrice] = useState(false)
  const [personnel, setPersonnel] = useState<IPersonnelModel | null>(null)
  const formikRef = useRef<any>(null) 

  type Tabs = 'quick' | 'res' | 'customer' | 'car' | 'price' | 'extra' | 'details'

  const tabOptions: TabOption<Tabs>[] = [
    {key: 'quick', title: 'Hızlı Giriş', icon: 'bi bi-lightning'},
    {key: 'res', title: 'Rez.Bilgisi/Teslimat', icon: 'bi bi-clipboard-check'},
    {key: 'customer', title: 'Müşteri', icon: 'bi bi-person'},
    {key: 'car', title: 'Araç / Grup', icon: 'bi bi-car-front'},
    {key: 'price', title: 'Fiyat / Toplam', icon: 'bi bi-cash-coin'},
    {key: 'extra', title: 'Ek Hizmet', icon: 'bi bi-plus-circle'},
    {key: 'details', title: 'Ayrıntılar', icon: 'bi bi-list-ul'},
  ]

  const user = useSelector((state: RootState) => state.auth.user)
  const userId = user?.id ?? '' 
  useEffect(() => {
    if (!userId) {
      return
    }
    const loadData = async () => {
      const [brn, cr, pr, tr, personnelRes] = await Promise.all([branches(), currencies(), personels(), tariffs(), getPersonnelByUserId(userId)])
      setBranchOptions(brn)
      setTariffOptions(tr)
      setCurrencyOptions(cr)
      setPersonelOptions(pr)
      const personnel = personnelRes.data?.payload 
      if (personnel) {
        setPersonnel(personnel) 
      }
    }

    loadData()
  }, [userId])

  useEffect(() => {
    if (!personnel?.branch || branchOptions.length === 0) return
    // Mevcut bir rezervasyonu görüntülerken/düzenlerken personelin
    // varsayılan şubesini formdaki değerlerin üzerine yazmıyoruz
    if (reservationIdFromUrl || selectedId) return
    const selected = branchOptions.find((o) => String(o.value) === String(personnel.branch))
    formikRef.current?.setFieldValue('pickupBranch', personnel.branch)
    formikRef.current?.setFieldValue('pickupBranchId', personnel.branch)
    formikRef.current?.setFieldValue('pickupBranchName', selected?.label ?? null)
    formikRef.current?.setFieldValue('pickupBranchAdd', selected?.label ?? null)
    formikRef.current?.setFieldValue('returnBranch', personnel.branch)
    formikRef.current?.setFieldValue('returnBranchId', personnel.branch)
    formikRef.current?.setFieldValue('returnBranchName', selected?.label ?? null)
    formikRef.current?.setFieldValue('returnBranchAdd', selected?.label ?? null)
    formikRef.current?.setFieldValue('operationBranch', personnel.branch)
  }, [personnel, branchOptions, reservationIdFromUrl, selectedId])

  // URL'den reservationId geldiğinde rezervasyonu yükle ve formu doldur
  useEffect(() => {
    if (!reservationIdFromUrl) return
    let cancelled = false
    setLoading(true)
    getReservationById(reservationIdFromUrl)
      .then((res) => {
        if (cancelled) return
        if (res.data.hasError || !res.data.payload) {
          showErrorToast(res.data.message || 'Rezervasyon bilgisi alınamadı')
          return
        }
        const reservation = res.data.payload
        setSelectedId(reservation.id ?? reservationIdFromUrl)
        setInitialFormValues({...initialReservation, ...reservation})
      })
      .catch((err) => {
        if (cancelled) return
        const e = err as {response?: {status?: number}}
        if (e?.response?.status === 401) {
          window.location.href = '/login'
          return
        }
        console.error(err)
        showErrorToast('Rezervasyon bilgisi yüklenemedi')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [reservationIdFromUrl])

  const handleSave = async (values: IReservationModel, resetForm: () => void) => {
    setLoading(true)
    try {
      if (selectedId) {
        await updateReservation(selectedId, values)
        showSuccessToast('rezervasyon başarıyla güncellendi')
      } else {
        await createReservation(values)
        showSuccessToast('rezervasyon başarıyla oluşturuldu')
      }
      resetForm()
      setSelectedId(null)
      setTab('quick')
    } catch (err) {
      showErrorToast('Bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const syncCarFields = (car: AsyncCarModel | null, setFieldValue: any) => {
    if (!car?.id) {
      ;[
        'car',
        'car1',
        'brand',
        'brand1',
        'modelType',
        'modelType1',
        'modelYear',
        'transmission',
        'transmission1',
        'fuelType',
        'fuelType1',
      ].forEach((f) => setFieldValue(f, ''))

      return
    }

    const transmission =
      TRANSMISSION_TYPES.find((t) => t.value === String(car.transmission))?.label ?? ''

    const fuelType = FUEL_TYPES.find((t) => t.value === String(car.fuelType))?.label ?? ''

    setFieldValue('car', car)
    setFieldValue('carId', car.id)
    setFieldValue('carPlate', car.plate)
    setFieldValue('plateAdd', car.plate)
    setFieldValue('carDetail', car)

    setFieldValue('brandName', car.brand)
    setFieldValue('brandNameDetail', car.brand)

    setFieldValue('modelType', car.modelType)
    setFieldValue('modelTypeDetail', car.modelType)

    setFieldValue('modelYear', car.modelYear)

    setFieldValue('transmission', transmission)
    setFieldValue('transmissionDetail', transmission)

    setFieldValue('fuelType', fuelType)
    setFieldValue('fuelTypeDetail', fuelType)
  }

  const syncStartDate = (
    source: 'startDate' | 'startDate1' | 'endDate' | 'endDate1',
    value: Date | null,
    setFieldValue: any
  ) => {
    if (source === 'startDate') {
      setFieldValue('startDate1', value)
      setFieldValue('startDateAdd', value)
    } else if (source === 'startDate1') {
      setFieldValue('startDate', value)
      setFieldValue('startDateAdd', value)
    } else if (source === 'endDate') {
      setFieldValue('endDate1', value)
      setFieldValue('endDateAdd', value)
    } else if (source === 'endDate1') {
      setFieldValue('endDate', value)
      setFieldValue('endDateAdd', value)
    }
  }

  const syncReservationTime = (
    source: 'startTime' | 'startTime1' | 'endTime' | 'endTime1',
    value: string | null,
    setFieldValue: any
  ) => {
    if (source === 'startTime') {
      setFieldValue('startTime1', value)
      setFieldValue('startTimeAdd', value)
    } else if (source === 'startTime1') {
      setFieldValue('startTime', value)
      setFieldValue('startTimeAdd', value)
    } else if (source === 'endTime') {
      setFieldValue('endTime1', value)
      setFieldValue('endTimeAdd', value)
    } else if (source === 'endTime1') {
      setFieldValue('endTime', value)
      setFieldValue('endTimeAdd', value)
    }
  }

  const parseDate = (value?: string | Date | null): Date | null => {
    if (!value) return null

    if (value instanceof Date) return value

    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  const setDriverFields = (
    source: 'driverFirst' | 'driverSecond',
    driver: AsyncCustomerModel | null,
    setFieldValue: any
  ) => {
    const prefix = source

    setFieldValue(`${prefix}.phone`, driver?.phone ?? '')
    setFieldValue(`${prefix}.tckn`, driver?.tcKimlikNo ?? '')
    setFieldValue(`${prefix}.licenseNo`, driver?.drivingLicenseNo ?? '')
    setFieldValue(`${prefix}.name`, driver?.firstName ?? '')
    setFieldValue(`${prefix}.surname`, driver?.lastName ?? '')
    setFieldValue(`${prefix}.licensePlace`, driver?.drivingLicenseIssuedBy ?? '')

    setFieldValue(`${prefix}.licenseDate`, parseDate(driver?.drivingLicenseIssueDate))

    setFieldValue(`${prefix}.birthDate`, parseDate(driver?.dateOfBirth))
  }

  const syncCustomerFields = (customer: AsyncCustomerModel | null, setFieldValue: any) => {
    if (!customer?.id) {
      ;[
        'customer',
        'customer1',
        'phone',
        'email',
        'tcIdentityNo',
        'birthday',
        'birthPlace',
        'driverLicenseNo',
        'licensePlace',
        'licenseDate',
        'district',
        'city',
        'country',
        'address',
        'passportNo',
        'passportPlace',
        'totalRentalCount',
      ].forEach((f) => setFieldValue(f, ''))
      return
    }
    setFieldValue('customer', customer)
    setFieldValue('customer1', customer)
    setFieldValue('customerId', customer ? customer.id : null)
    setFieldValue(
      'customerFullName',
      customer ? customer.firstName + ' ' + customer.lastName : null
    )
    setFieldValue('customerAdd', customer ? customer.firstName + ' ' + customer.lastName : null)
    setFieldValue('phone', customer.phone)
    setFieldValue('email', customer.emailAddress)
    setFieldValue('tcIdentityNo', customer.tcKimlikNo)
    setFieldValue('birthday', customer.dateOfBirth)
    setFieldValue('birthPlace', customer.placeOfBirth)
    setFieldValue('driverLicenseNo', customer.drivingLicenseNo)
    setFieldValue('licensePlace', customer.drivingLicenseIssuedBy)
    setFieldValue('licenseDate', customer.drivingLicenseIssueDate)
    setFieldValue('district', customer.district)
    setFieldValue('city', customer.city)
    setFieldValue('country', customer.licenseCountry)
    setFieldValue('address', customer.address)
    setFieldValue('passportNo', customer.passportNumber)
    setFieldValue('passportPlace', customer.passportIssuedBy)
    setFieldValue('totalRentalCount', customer.totalRentalCount)
  }

  const syncCurrency = (
    source: 'currency' | 'currency1' | 'remainingCurrency' | 'remainingCurrency1',
    value: string | '',
    setFieldValue: any
  ) => {
    const map = {
      currency: 'currency1',
      currency1: 'currency',
      currencyAdd: 'currency',
      generalCurrencyAdd: 'currency',
      remainingCurrency: 'remainingCurrency1',
      remainingCurrency1: 'remainingCurrency',
    } as const

    setFieldValue(map[source], value)
    const currency = CURRENCIES.find((t) => t.value === String(value))?.label ?? ''
    setCurrency(currency)
  }

  const syncReservationSource = async (
    rSource: AsyncReservationSourceModel | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setFieldValue('reservationSource', rSource)
    setFieldValue('reservationSourceDetail', rSource)
    setFieldValue('reservationSourceId', rSource?.id)
    if (rSource != null) {
      setIsSelectedResSource(false)
      setIsManualPrice(false)
    } else {
      setIsSelectedResSource(false)
      setIsManualPrice(true)
      resetCalculateValues(setFieldValue)
    }
  }

  const resetCalculateValues = (setFieldValue: (field: string, value: any) => void) => {
    setFieldValue('price', 0)
    setFieldValue('price1', 0)
    setFieldValue('priceAdd', 0)
    setFieldValue('discount1', 0)
    setFieldValue('totalAmount', 0)
    setFieldValue('subTotalAmount', 0)
    setFieldValue('totalAmount1', 0)
    setFieldValue('totalAmountAdd', 0)
    setFieldValue('rentalAmount', 0)
    setFieldValue('rentAmount1', 0)
    setFieldValue('taxAmount', 0)
    setFieldValue('kdvTl1', 0)
    setFieldValue('generalTotalWithCurrency', 0)
    setFieldValue('generalTotalWithCurrencyAdd', 0)
    setFieldValue('grandTotal', 0)
    setFieldValue('priceAfterDiscount', 0)
    setFieldValue('priceAfterDiscount1', 0)
    setFieldValue('remainingAmount', 0)
    setFieldValue('remaining1', 0)
    setRemainingAmount(0)
  }

  const syncCalculationType = (type: string, setFieldValue: any) => {
    setFieldValue('calculationType', type)
    setFieldValue('pricingType', type)
    setFieldValue('calculateTypeAdd', type)
  }

  const [selectedServicesTotal, setSelectedServicesTotal] = useState(0)

  const next = (to: Tabs) => setTab(to)
  const back = (to: Tabs) => setTab(to)

  return (
    <>
      <PageTitle breadcrumbs={[]}>Yeni Rezervasyon Ekranı</PageTitle>

      <Formik
        innerRef={formikRef}
        initialValues={initialFormValues}
        enableReinitialize
        validationSchema={validationReservation}
        onSubmit={(values, {resetForm}) => handleSave(values, resetForm)}
      >
        {(formik) => {
          const handleReset = () => formik.resetForm()
          return (
            <>
              <AdditionalServicesCalculator
                formik={formik}
                setSelectedServicesTotal={setSelectedServicesTotal}
              />
              <FormWatcher/>
              <CalculateRentalPrice              
                setRemainingAmount={setRemainingAmount}
                addinionalServiceAmount={selectedServicesTotal}
                isManualPrice={isManualPrice}
              />
              <FormTabHeader
                formId='reservationForm'
                formikSubmit={formik.handleSubmit}
                formikReset={handleReset}
                loading={loading}
                tab={tab}
                setTab={setTab as React.Dispatch<React.SetStateAction<string>>}
                setSelectedId={() => {}}
                tabs={tabOptions}
                onExportExcel={() => console.log('Excel export')}
                smallButton={true}
              />

              <div className='card mb-5'>
                <Form id='reservationForm' className='form card-body'>
                  {tab === 'quick' && (
                    <>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncCustomerModel>
                          name='customer'
                          label='Cari Ara'
                          placeholder='Ad Soyad girin...'
                          fetchHandler={fetchAsyncCustomerData}
                          displayField={(item) => `${item.firstName} ${item.lastName}`}
                          onSelect={(item) => syncCustomerFields(item, formik.setFieldValue)}
                          onClear={() => syncCustomerFields(null, formik.setFieldValue)}
                          wrapperClassName='col-md-6'
                          modalId='modal_customer_search'
                          showModal={showCustomerModal}
                          onModalToggle={setShowCustomerModal}
                        />
                        <SelectInput
                          name='status'
                          label='Durum'
                          wrapperClassName='col-md-3'
                          options={RESERVATION_STATUS}
                        />
                        <DateInput
                          name='reservationDate'
                          type='date'
                          label='Rezervasyon Tarihi'
                          wrapperClassName='col-md-3'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='fileNumber1'
                          label='Dosya No 1'
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='fileNumber2'
                          label='Dosya No 2'
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='flightNumber'
                          label='Geliş Uçuş No'
                          wrapperClassName='col-md-6'
                        />
                      </div>

                      <div className='row mb-2'>
                        <DateInput
                          name='startDate'
                          type='date'
                          label='Başlangıç Tarihi'
                          wrapperClassName='col-md-3'
                          onDateChange={(date) =>
                            syncStartDate('startDate', date, formik.setFieldValue)
                          }
                        />
                        <TextInput
                          name='startTime'
                          type='time'
                          label='Başlangıç Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('startTime', time, formik.setFieldValue)
                          }
                        />
                        <div className='col-md-1'></div>
                        <SelectInput
                          name='rentalType'
                          label='Kiralama Türü'
                          wrapperClassName='col-md-3'
                          options={RENTAL_TYPES}
                        />
                        <SelectInput
                          name='billingType'
                          label='Faturalama'
                          wrapperClassName='col-md-3'
                          options={PAYMENT_OWNER}
                        />
                      </div>
                      <div className='row mb-2'>
                        <DateInput
                          name='endDate'
                          type='date'
                          label='Beklenen Tarih'
                          wrapperClassName='col-md-3'
                          onDateChange={(date) =>
                            syncStartDate('endDate', date, formik.setFieldValue)
                          }
                        />
                        <TextInput
                          name='endTime'
                          type='time'
                          label='Beklenen Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('endTime', time, formik.setFieldValue)
                          }
                        />
                        <TextInput
                          name='rentalDay'
                          label='Gün'
                          readOnly={true}
                          wrapperClassName='col-md-1'
                        />
                        <AsyncSearchInput<AsyncCustomerModel>
                          name='customerCompany'
                          label='Müşterinin Firması'
                          placeholder='Firma adı girin...'
                          fetchHandler={fetchAsyncCustomerData}
                          displayField={(item) => `${item.firstName} ${item.lastName}`}
                          onSelect={(item) => formik.setFieldValue('customerCompanyId', item.id)}
                          onClear={() => formik.setFieldValue('customerCompanyId', '')}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='pickupBranch'
                          label='Çıkış Ofisi'
                          wrapperClassName='col-md-3'
                          defaultValue={personnel?.branch}
                          options={branchOptions}
                          onChange={(value) => {
                            const selected = branchOptions.find(
                              (o) => String(o.value) === String(value)
                            )
                            formik.setFieldValue('pickupBranchId', value)
                            formik.setFieldValue('pickupBranchName', selected?.label ?? null)
                            formik.setFieldValue('pickupBranchAdd', selected?.label ?? null)
                          }}
                        />
                        <TextInput
                          name='pickupBranchNote'
                          label='Çıkış Yeri Notu'
                          wrapperClassName='col-md-3'
                        />
                        <SelectInput
                          name='calculationType'
                          label='Hesaplama Tipi'
                          wrapperClassName='col-md-3'
                          options={CALCULATION_TYPES}
                          onChange={(value) =>
                            syncCalculationType(value as string | '0', formik.setFieldValue)
                          }
                        />
                        <SelectInput
                          name='currency'
                          label='Döviz'
                          wrapperClassName='col-md-3'
                          options={currencyOptions}
                          onChange={(value) =>
                            syncCurrency('currency', value as string | '1', formik.setFieldValue)
                          }
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='returnBranch'
                          label='Dönüş Ofisi'
                          wrapperClassName='col-md-3'
                          defaultValue={personnel?.branch}
                          options={branchOptions}
                          onChange={(value) => {
                            const selected = branchOptions.find(
                              (o) => String(o.value) === String(value)
                            )
                            formik.setFieldValue('returnBranchId', value)
                            formik.setFieldValue('returnBranchName', selected?.label ?? null)
                            formik.setFieldValue('returnBranchAdd', selected?.label ?? null)
                          }}
                        />
                        <TextInput
                          name='returnBranchNote'
                          label='Dönüş Yeri Notu'
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='price'
                          type={isSelectedResSource ? 'password' : 'text'}
                          currency={true}
                          label='Fiyat'
                          wrapperClassName='col-md-2'
                          onChange={() => {
                            setIsManualPrice(true)
                          }}
                        />
                        <TextInput name='discount' label='İndirim' wrapperClassName='col-md-2' />
                        <TextInput
                          name='priceAfterDiscount'
                          type={isSelectedResSource ? 'password' : 'text'}
                          currency={true}
                          label='İnd. Sonrası Fiyat'
                          readOnly={true}
                          wrapperClassName='col-md-2'
                        />
                      </div>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncVehicleGroupModel>
                          name='vehicleGroup'
                          label='Araç Grubu Ara'
                          placeholder='Araç grubu girin...'
                          fetchHandler={fetchAsyncVehicleGroupData}
                          displayField={(item) => `${item.groupName}`}
                          onSelect={(item) => {
                            formik.setFieldValue('vehicleGroup', item)
                            formik.setFieldValue('vehicleGroupId', item.id)
                            formik.setFieldTouched('vehicleGroup', true, false)
                          }}
                          onClear={() => {
                            formik.setFieldValue('vehicleGroup', null)
                            formik.setFieldValue('vehicleGroupId', '')
                            formik.setFieldTouched('vehicleGroup', true, false)
                          }}
                          clearValue={{id: '', groupName: ''}}
                          wrapperClassName='col-md-6'
                        />
                        <AsyncSearchInput<AsyncReservationSourceModel>
                          name='reservationSource'
                          label='Rezervasyon Kaynağı'
                          placeholder='Rezervasyon kaynağı adı girin...'
                          fetchHandler={fetchAsyncReservationSourceData}
                          displayField={(item) => `${item.reservationSourceName}`}
                          onSelect={(item) => syncReservationSource(item, formik.setFieldValue)}
                          onClear={() => syncReservationSource(null, formik.setFieldValue)}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncCarModel>
                          name='car'
                          label='Araç Ara'
                          placeholder='Araç plaka girin...'
                          fetchHandler={fetchAsyncCarData}
                          displayField={(item) => `${item.plate} (${item.modelYear})`}
                          onSelect={(item) => syncCarFields(item, formik.setFieldValue)}
                          onClear={() => syncCarFields(null, formik.setFieldValue)}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='deliveredPersonelId'
                          label='Teslim Eden'
                          options={personelOptions}
                          wrapperClassName='col-md-6'
                        />
                      </div>

                      <div className='row g-3'>
                        <div className='col-12 col-lg-6'>
                          <div className='row g-3'>
                            <TextInput
                              name='brandName'
                              label='Marka'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='modelType'
                              label='Tip'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='transmission'
                              label='Vites'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='fuelType'
                              label='Yakıt'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='givenGroup'
                              label='Verilen Araç Grubu'
                              readOnly={true}
                              wrapperClassName='col-12 col-md-12'
                            />
                          </div>
                        </div>
                        <div className='col-12 col-lg-6'>
                          <div className='row g-3'>
                            <TextInput
                              name='rentalAmount'
                              type={isSelectedResSource ? 'password' : 'text'}
                              currency={true}
                              label='Kira Tutarı (TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='additionalServiceAmount'
                              currency={true}
                              label='Ek Hiz.Tut.(TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='totalAmount'
                              currency={true}
                              label='Top. Tutar(TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='taxAmount'
                              currency={true}
                              label='Kdv Tutarı (TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='subTotalAmount'
                              currency={true}
                              label='Toplam(TL)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='generalTotalWithCurrency'
                              currency={true}
                              label='Toplam(Döviz)'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <TextInput
                              name='remainingAmount'
                              currency={true}
                              label='Kalan'
                              readOnly={true}
                              wrapperClassName='col-6 col-xl-3'
                            />
                            <SelectInput
                              name='remainingCurrency'
                              label='Kalan Döviz'
                              options={currencyOptions}
                              wrapperClassName='col-6 col-xl-3'
                              onChange={(value) =>
                                syncCurrency(
                                  'remainingCurrency',
                                  value as string | '1',
                                  formik.setFieldValue
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>

                      <div className='badge-container'>
                        <span className='custom-badge'>
                          <span className='label'>Rez. Kaynağı</span>
                          <span className='value'>0.00 TL</span>
                        </span>
                        <span className='custom-badge'>
                          <span className='label'>Müşteri</span>
                          <span className='value'>0.00 TL</span>
                        </span>
                        <span className='custom-badge'>
                          <span className='label'>Tahsilat</span>
                          <span className='value'>0.00 TL</span>
                        </span>
                        <span className='custom-badge'>
                          <span className='label'>Kalan</span>
                          <span className='value'>
                            {remainingAmount} {currency}
                          </span>
                        </span>
                      </div>

                      <TabNavigationButtons onNext={() => next('res')} />
                    </>
                  )}

                  {tab === 'res' && (
                    <>
                      <div className='row mb-2'>
                        <SelectInput
                          name='operationBranch'
                          label='İşlem Şube'
                          wrapperClassName='col-md-6'
                          options={branchOptions}
                          defaultValue={personnel?.branch}
                        />
                        <div className='col-md-6'>
                          <div className='d-flex gap-2'>
                            <SelectInput
                              name='optionType'
                              label='Opsiyon Tipi'
                              wrapperClassName='flex-fill'
                              options={OPTION_TYPES}
                            />
                            <TextInput
                              name='optionDay'
                              label='Gün'
                              type='number'
                              wrapperClassName='flex-fill'
                            />
                            <TextInput
                              name='optionDate'
                              type='date'
                              label='Tarih'
                              wrapperClassName='flex-fill'
                            />
                          </div>
                        </div>
                      </div>

                      <div className='row mb-2'>
                        <SelectInput
                          name='reservedPersonelId'
                          label='Rezervasyonu Alan'
                          wrapperClassName='col-md-6'
                          options={personelOptions}
                          defaultValue={personnel?.id ?? undefined as string | boolean | string[] | undefined}
                        />
                        <TextInput
                          name='giftDay'
                          type='number'
                          label='Hediye Gün'
                          wrapperClassName='col-md-6'
                        />
                      </div>

                      <div className='row mb-2'>
                        <DateInput
                          name='startDate1'
                          type='date'
                          label='Başlangıç Tarihi'
                          wrapperClassName='col-md-4'
                          onDateChange={(date) =>
                            syncStartDate('startDate1', date, formik.setFieldValue)
                          }
                        />
                        <TextInput
                          name='startTime1'
                          type='time'
                          label='Başlangıç Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('startTime1', time, formik.setFieldValue)
                          }
                        />
                      </div>

                      <div className='row mb-2'>
                        <TextInput
                          name='expectedDay'
                          type='number'
                          label='Gün'
                          readOnly={true}
                          wrapperClassName='col-md-1'
                        />
                        <DateInput
                          name='endDate1'
                          type='date'
                          label='Beklenen Tarih'
                          wrapperClassName='col-md-3'
                          onDateChange={(date) =>
                            syncStartDate('endDate1', date, formik.setFieldValue)
                          }
                        />
                        <TextInput
                          name='endTime1'
                          type='time'
                          label='Beklenen Saat'
                          wrapperClassName='col-md-2'
                          onValueChange={(time) =>
                            syncReservationTime('endTime1', time, formik.setFieldValue)
                          }
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('quick')} onNext={() => next('customer')} />
                    </>
                  )}

                  {tab === 'customer' && (
                    <>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncCustomerModel>
                          name='customer1'
                          label='Cari Ara'
                          placeholder='Ad Soyad girin...'
                          fetchHandler={fetchAsyncCustomerData}
                          displayField={(item) => `${item.firstName} ${item.lastName}`}
                          onSelect={(item) => syncCustomerFields(item, formik.setFieldValue)}
                          onClear={() => syncCustomerFields(null, formik.setFieldValue)}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='driverLicenseNo'
                          label='Ehliyet No'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <PhoneInput name='phone' label='Cep Tel' wrapperClassName='col-md-6' />
                        <AsyncSearchInput<AsyncReservationSourceModel>
                          name='reservationSourceDetail'
                          label='Rezervasyon Kaynağı 1'
                          placeholder='Kayıtlı bir rezervasyon kaynağı arayın ve seçin'
                          fetchHandler={fetchAsyncReservationSourceData}
                          displayField={(item) => `${item.reservationSourceName}`}
                          onSelect={(item) => syncReservationSource(item, formik.setFieldValue)}
                          onClear={() => syncReservationSource(null, formik.setFieldValue)}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='email'
                          type='email'
                          label='Mail Adresi'
                          wrapperClassName='col-md-6'
                        />
                        <AsyncSearchInput<AsyncReservationSourceModel>
                          name='secondReservationSource'
                          label='Rezervasyon Kaynağı 2'
                          placeholder='Kayıtlı bir rezervasyon kaynağı arayın ve seçin'
                          fetchHandler={fetchAsyncReservationSourceData}
                          displayField={(item) => `${item.reservationSourceName}`}
                          onSelect={(item) =>
                            formik.setFieldValue('secondReservationSourceId', item.id)
                          }
                          onClear={() => formik.setFieldValue('secondReservationSourceId', '')}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='tcIdentityNo'
                          label='TC Kimlik No'
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='licensePlace'
                          label='Ehliyet Yer'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput name='district' label='İlçe' wrapperClassName='col-md-6' />
                        <TextInput
                          name='licenseDate'
                          label='Ehliyet Tarih'
                          type='date'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput name='city' label='Şehir' wrapperClassName='col-md-6' />
                        <TextInput
                          name='birthPlace'
                          label='Doğum Yeri'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput name='country' label='Ülke' wrapperClassName='col-md-6' />
                        <TextInput
                          name='birthday'
                          label='Doğum Tarihi'
                          type='date'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='totalRentalCount'
                          label='Toplam Kira Adeti'
                          type='number'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='passportNo'
                          label='Pasaport No'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextAreaInput name='address' label='Adres' wrapperClassName='col-md-6' />
                        <TextInput
                          name='passportPlace'
                          label='Pasaport Yeri'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('res')} onNext={() => next('car')} />
                    </>
                  )}

                  {tab === 'car' && (
                    <>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncVehicleGroupModel>
                          name='vehicleGroup1'
                          label='Araç Grubu'
                          placeholder='Araç grubu girin...'
                          fetchHandler={fetchAsyncVehicleGroupData}
                          displayField={(item) => item.groupName}
                          onSelect={(item) => console.log('Seçilen araç grubu:', item)}
                          onClear={() => console.log('Araç grubu temizlendi')}
                          clearValue={{id: '', groupName: ''}}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='brandNameDetail'
                          label='Marka'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                      </div>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncVehicleGroupModel>
                          name='deliveredVehicleGroup'
                          label='Verilen Araç Grubu'
                          placeholder='Araç grubu girin...'
                          fetchHandler={fetchAsyncVehicleGroupData}
                          displayField={(item) => item.groupName}
                          onSelect={(item) => console.log('Seçilen araç grubu:', item)}
                          onClear={() => console.log('Araç grubu temizlendi')}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='modelYear'
                          label='Yılı'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                      </div>
                      <div className='row mb-2'>
                        <AsyncSearchInput<AsyncCarModel>
                          name='carDetail'
                          label='Araç Ara'
                          placeholder='Araç plaka giriniz...'
                          fetchHandler={fetchAsyncCarData}
                          displayField={(car) => `${car.plate} (${car.modelYear})`}
                          onSelect={(item) => syncCarFields(item, formik.setFieldValue)}
                          onClear={() => syncCarFields(null, formik.setFieldValue)}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='fuelTypeDetail'
                          label='Yakıt Türü'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='modelTypeDetail'
                          label='Tipi'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                        <TextInput
                          name='fullnessAccount'
                          label='Doluluk Hesap'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='transmissionDetail'
                          label='Vites'
                          wrapperClassName='col-md-6'
                          readOnly={true}
                        />
                        <TextInput
                          name='carLocation'
                          label='Araç Yeri'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='carDetails'
                          label='Araç Detayı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('customer')} onNext={() => next('price')} />
                    </>
                  )}

                  {tab === 'price' && (
                    <>
                      <div className='row mb-2'>
                        <SelectInput
                          name='pricingType'
                          label='Fiyatlandırma'
                          options={CALCULATION_TYPES}
                          wrapperClassName='col-md-6'
                          onChange={(value) =>
                            syncCalculationType(value as string | '0', formik.setFieldValue)
                          }
                        />
                        <TextInput
                          name='rentalDay'
                          label='Kira Gün'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='price1'
                          currency={true}
                          label='Fiyat'
                          wrapperClassName='col-md-2'
                        />
                        <TextInput
                          name='discount1'
                          type='number'
                          label='İndirim'
                          wrapperClassName='col-md-2'
                        />
                        <TextInput
                          name='priceAfterDiscount1'
                          currency={true}
                          label='İnd. Sonrası Fiyat'
                          readOnly={true}
                          wrapperClassName='col-md-2'
                        />
                        <SelectInput
                          name='currency1'
                          label='Döviz'
                          options={currencyOptions}
                          wrapperClassName='col-md-6'
                          onChange={(value) =>
                            syncCurrency('currency1', value as string | '1', formik.setFieldValue)
                          }
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='autoDesc'
                          label='Otomatik Açıklama'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <SelectInput
                          name='campaign'
                          label='Kampanya'
                          options={[{label: 'Örnek Kampanya', value: '0'}]}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='deliveryType'
                          label='Teslimat Şekli'
                          options={DELIVERY_TYPES}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='campaignCondition'
                          label='Kampanya Şartı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='tariffId'
                          label='Tarife'
                          options={tariffOptions}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='customVatRate'
                          type='number'
                          label='Özel KDV Oranı'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='rentAmount1'
                          label='Kira Tutarı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='totalAmount1'
                          label='Toplam Tutar'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='serviceAmount1'
                          label='Hizmet Tutarı'
                          readOnly={true}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='kdvTl1'
                          label='KDV'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                        <TextInput
                          name='grandTotal'
                          label='Genel Toplam'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                      </div>
                      <div className='row mb-2'>
                        <TextInput
                          name='promotionCode'
                          label='Promosyon Kodu'
                          wrapperClassName='col-md-2'
                        />
                        <TextInput
                          name='promotionAmount'
                          label='Tutar'
                          wrapperClassName='col-md-2'
                        />
                        <SelectInput
                          name='promotionCurrency'
                          label='Döviz'
                          options={currencyOptions}
                          wrapperClassName='col-md-2'
                        />
                        <TextInput
                          name='collection'
                          label='Tahsilat'
                          readOnly={true}
                          currency={true}
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='paymentMethod'
                          label='Ödeme Şekli'
                          options={PAYMENT_TYPES}
                          wrapperClassName='col-md-6'
                        />
                        <TextInput
                          name='remaining1'
                          label='Kalan'
                          readOnly={true}
                          wrapperClassName='col-md-3'
                        />
                        <SelectInput
                          name='remainingCurrency1'
                          label='Kalan Döviz'
                          options={currencyOptions}
                          wrapperClassName='col-md-3'
                          onChange={(value) =>
                            syncCurrency(
                              'remainingCurrency1',
                              value as string | '1',
                              formik.setFieldValue
                            )
                          }
                        />
                      </div>
                      <div className='row mb-2'>
                        <CheckboxInput
                          name='hourDifferenceTaken'
                          label='Saat Farkını Alma'
                          wrapperClassName='col-md-6'
                        />
                      </div>
                      <TabNavigationButtons onBack={() => back('car')} onNext={() => next('extra')} />
                    </>
                  )}

                  {tab === 'extra' && (
                    <>
                      <div className='mb-5'>
                        <div className='row mb-2'>
                          <TextInput
                            name='customerAdd'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                          <TextInput
                            name='pickupBranchAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='returnBranchAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='priceAdd'
                            readOnly={true}
                            currency={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='generalCurrencyAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                        </div>
                        <div className='row mb-2'>
                          <DateInput
                            name='startDateAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='startTimeAdd'
                            type='time'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='rentDayAdd'
                            readOnly={true}
                            wrapperClassName='col-md-1'
                          />
                          <TextInput name='plateAdd' readOnly={true} wrapperClassName='col-md-3' />
                          <TextInput
                            name='calculateTypeAdd'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                        </div>
                        <div className='row mb-2'>
                          <DateInput
                            name='endDateAdd'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='endTimeAdd'
                            type='time'
                            readOnly={true}
                            wrapperClassName='col-md-2'
                          />
                          <TextInput
                            name='vehicleGroupAdd'
                            readOnly={true}
                            wrapperClassName='col-md-4'
                          />
                          <div className='col-md-4'>
                            <div className='row mb-2'>
                              <TextInput
                                name='totalAmountAdd'
                                readOnly={true}
                                currency={true}
                                wrapperClassName='col-md-4'
                              />
                              <TextInput
                                name='remainingAmountAdd'
                                readOnly={true}
                                currency={true}
                                wrapperClassName='col-md-5'
                              />
                              <TextInput
                                name='currencyAdd'
                                readOnly={true}
                                wrapperClassName='col-md-3'
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className='row mb-2'>
                        <SelectInput
                          name='additionalCurrencyAdd'
                          label='Hizmet Döviz'
                          wrapperClassName='col-md-4'
                          options={currencyOptions}
                          labelPosition='left'
                        />
                        <SelectInput
                          name='additionalServiceSellOwner'
                          label='Hizmet Satan'
                          wrapperClassName='col-md-5'
                          firstItemLabel='Ek Hizmet Satanı Seçiniz'
                          options={personelOptions}
                          labelPosition='left'
                        />
                        <CheckboxInput
                          name='fullCredit'
                          label='Full Credit'
                          wrapperClassName='col-md-3'
                        />
                      </div>
                      {formik.values.additionalServices?.map((service, index) => {
                        const calculateTotal = (
                          index: number,
                          changedField?: 'piece' | 'price',
                          newValue?: string | null
                        ) => {
                          const service = formik.values.additionalServices![index]

                          const pieceValue =
                            changedField === 'piece'
                              ? Number(newValue ?? 0)
                              : Number(service.piece || 0)

                          const priceValue =
                            changedField === 'price'
                              ? Number(newValue ?? 0)
                              : Number(service.price || 0)

                          const total = service.hasPiece ? pieceValue * priceValue : priceValue

                          formik.setFieldValue(
                            `additionalServices[${index}].totalPrice`,
                            Number(total.toFixed(2)),
                            false
                          )
                        }

                        return (
                          <div className='row mb-6' key={service.name}>
                            <CheckboxInput
                              name={`additionalServices[${index}].isAdd`}
                              label={service.title}
                              wrapperClassName={
                                service.hasPiece
                                  ? service.hasProduct
                                    ? 'col-md-3 pt-8'
                                    : 'col-md-3'
                                  : service.hasProduct
                                  ? 'col-md-4 pt-8'
                                  : 'col-md-4'
                              }
                            />
                            {service.hasPiece && (
                              <TextInput
                                name={`additionalServices[${index}].piece`}
                                type='number'
                                label={service.hasProduct ? 'Adet' : ''}
                                wrapperClassName='col-md-1 ps-0'
                                onValueChange={(value) =>
                                  service.isDaily && calculateTotal(index, 'piece', value)
                                }
                              />
                            )}
                            <div className='col-md-5'>
                              <div className='row'>
                                {service.isDaily ? (
                                  <TextInput
                                    name={`additionalServices[${index}].price`}
                                    currency={true}
                                    label={service.hasProduct ? 'Günlük' : ''}
                                    labelPosition='top'
                                    wrapperClassName='col-md-4 ps-0'
                                    onValueChange={(value) => calculateTotal(index, 'price', value)}
                                  />
                                ) : (
                                  <div className='col-md-4 ps-0' />
                                )}

                                {service.hasProduct ? (
                                  <>
                                    <TextInput
                                      name={`additionalServices[${index}].productNumber`}
                                      placeholder='Koltuk Etiketi'
                                      wrapperClassName='col-md-4 pt-8 ps-0'
                                    />
                                    <SelectInput
                                      name={`additionalServices[${index}].productOwner`}
                                      wrapperClassName='col-md-4 pt-8 ps-0'
                                      options={PRODUCT_OWNER}
                                      defaultValue='0'
                                    />
                                  </>
                                ) : (
                                  <>
                                    <div className='col-md-2 ps-0' />
                                    <div className='col-md-2 ps-0' />
                                  </>
                                )}
                              </div>
                            </div>
                            <div className='col-md-3'>
                              <div className='row'>
                                <TextInput
                                  name={`additionalServices[${index}].totalPrice`}
                                  label={service.hasProduct ? 'Toplam Bedel' : ''}
                                  currency={true}
                                  wrapperClassName='col-md-7 ps-0'
                                  readOnly={service.isDaily}
                                />
                                <SelectInput
                                  name={`additionalServices[${index}].status`}
                                  label={service.hasProduct ? 'Durum' : ''}
                                  wrapperClassName='col-md-5 ps-0'
                                  options={ADDITIONAL_SERVICE_STATUS}
                                  defaultValue='0'
                                />
                              </div>
                            </div>
                          </div>
                        )
                      })}
                      <TabNavigationButtons onBack={() => back('price')} onNext={() => next('details')} />
                    </>
                  )}

                  {tab === 'details' && (
                    <>
                      <SubTabHeader
                        tab={tabSub}
                        setTab={setTabSub}
                        tabs={reservationSubTabOptions}
                      />
                      {tabSub === 'desc' && (
                        <>
                          <TextAreaInput
                            name='explanation'
                            label='Açıklama'
                            wrapperClassName='mb-6'
                          />
                        </>
                      )}
                      {tabSub === 'flight' && (
                        <>
                          <div className='row mb-2'>
                            <TextInput
                              name='accountOwner'
                              label='Hesap Sahibi' 
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='arrivalDate'
                              type='date'
                              label='Geliş Zamanı' 
                              wrapperClassName='col-md-4'
                            />
                            <TextInput name='arrivalTime' type='time' label='Geliş Saati' wrapperClassName='col-md-2' />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='accountNo'
                              label='No' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='returnFlightNo'
                              label='Dönüş Uçuş No' 
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-2'>
                            <DateInput
                              name='flightDate'
                              type='date'
                              label='Tarih' 
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='returnDate'
                              type='date'
                              label='Dönüş Zamanı' 
                              wrapperClassName='col-md-4'
                            />
                            <TextInput name='returnTime' type='time' label='Dönüş Saati' wrapperClassName='col-md-2' />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='security'
                              label='Güvenlik' 
                              wrapperClassName='col-md-6'
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'drivers' && (
                        <>
                          {/* 1. Sürücü */}
                          <div className='row mb-2'>
                            <AsyncSearchInput<AsyncCustomerModel>
                              name='driverFirst.driver'
                              label='1. Sürücüyü Ara'
                              placeholder='Ad Soyad girin...'
                              fetchHandler={fetchAsyncCustomerData}
                              displayField={(item) => `${item.firstName} ${item.lastName}`}
                              onSelect={(item) =>
                                setDriverFields('driverFirst', item, formik.setFieldValue)
                              }
                              onClear={() =>
                                setDriverFields('driverFirst', null, formik.setFieldValue)
                              }
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverFirst.phone'
                              label='Telefon' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverFirst.tckn'
                              label='TC No'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverFirst.licenseNo'
                              label='Ehliyet No'
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverFirst.name'
                              label='Ad'
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverFirst.licensePlace'
                              label='Ehliyet Veren Yer'
                              wrapperClassName='col-md-6' 
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverFirst.surname'
                              label='Soyad'
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='driverFirst.licenseDate'
                              type='date'
                              label='Ehliyet Veriliş Tarihi' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <DateInput
                              name='driverFirst.birthDate'
                              type='date'
                              label='Doğum Tarihi' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          {/* 2. Sürücü */}
                          <div className='row mb-2'>
                            <AsyncSearchInput<AsyncCustomerModel>
                              name='driverSecond.driver'
                              label='2. Sürücüyü Ara'
                              placeholder='Ad Soyad girin...'
                              fetchHandler={fetchAsyncCustomerData}
                              displayField={(item) => `${item.firstName} ${item.lastName}`}
                              onSelect={(item) =>
                                setDriverFields('driverSecond', item, formik.setFieldValue)
                              }
                              onClear={() =>
                                setDriverFields('driverSecond', null, formik.setFieldValue)
                              }
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverSecond.phone'
                              label='Telefon' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverSecond.tckn'
                              label='TC No' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverSecond.licenseNo'
                              label='Ehliyet No' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverSecond.name'
                              label='Ad' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='driverSecond.licensePlace'
                              label='Ehliyet Veren Yer' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='driverSecond.surname'
                              label='Soyad' 
                              wrapperClassName='col-md-6'
                            />
                            <DateInput
                              name='driverSecond.licenseDate'
                              type='date'
                              label='Ehliyet Veriliş Tarihi' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <DateInput
                              name='driverSecond.birthDate'
                              type='date'
                              label='Doğum Tarihi' 
                              wrapperClassName='col-md-6'
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'other' && (
                        <>
                          <div className='row mb-2'>
                            <TextInput
                              name='manualFindexScore'
                              label='Manuel Findex Puanı' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='guarantor'
                              label='Kefil' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='customerVehicle'
                              label='Müşteri Araç' 
                              wrapperClassName='col-md-6'
                            />
                            <TextAreaInput
                              name='warningDescription'
                              label='Uyarı Açıklama' 
                              wrapperClassName='col-md-6'
                              rows={3}
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='vehicleDetails'
                              label='Araç Detayı' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='specialCode'
                              label='Özel Kod' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='requestType'
                              label='Talep Türü' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='companyCode'
                              label='Firma Kodu' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='requestSourceUnit'
                              label='Talep Geldiği Birim' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='approvalCode'
                              label='Onay Kodu' 
                              wrapperClassName='col-md-6'
                            />
                          </div>

                          <div className='row mb-2'>
                            <TextInput
                              name='projectName'
                              label='Proje Adı' 
                              wrapperClassName='col-md-6'
                            />
                            <SelectInput
                              name='riskPermissionLevel'
                              label='Risk İzinli' 
                              wrapperClassName='col-md-6'
                              options={[
                                {value: '0', label: 'Normal'},
                                {value: '1', label: 'Düşük'},
                                {value: '2', label: 'Yüksek'},
                              ]}
                            />
                          </div>
                        </>
                      )}
                      {tabSub === 'broker' && (
                        <>
                          <div className='row mb-2'>
                            <TextInput
                              name='incomingRent'
                              label='Gelen Kira' 
                              wrapperClassName='col-md-6'
                            />
                            <TextInput
                              name='incomingDrop'
                              label='Gelen Drop' 
                              wrapperClassName='col-md-6'
                            />
                          </div>
                          <div className='row mb-2'>
                            <TextInput
                              name='incomingExtra'
                              label='Gelen Ekstra' 
                              wrapperClassName='col-md-6'
                            />
                            <SelectInput
                              name='incomingPayment'
                              label='Gelen Ödeme' 
                              wrapperClassName='col-md-6'
                              options={[
                                {value: '0', label: 'Normal'},
                                {value: '1', label: 'Düşük'},
                                {value: '2', label: 'Yüksek'},
                              ]}
                            />
                          </div>
                        </>
                      )}
                      <TabNavigationButtons onBack={() => back('price')} isSubmit nextLabel='Devam' />
                    </>
                  )}
                </Form>
              </div>
            </>
          )
        }}
      </Formik>

      <CustomerCreateModal
        show={showCustomerModal}
        modalId='modal_customer_search'
        handleClose={() => setShowCustomerModal(false)}
      />
      <ToastContainer
        position='top-right'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}
